/**
 * Hook para regenerar itens do pedido a partir da proposta atualizada
 * 
 * ESTRATÉGIA SEGURA:
 * 1. Pré-validar que a proposta gera itens válidos ANTES de deletar
 * 2. Preservar campos operacionais por linha_id
 * 3. Rollback em caso de erro
 * 
 * Usado quando o consultor "Revisa Proposta" e salva alterações
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useConverterPropostaEmItens, invalidarCacheConversor, resolverPlano, criarItensAparelhoFaltantes, criarItensPassaportFaltantes, itensPassaportForaDaProposta, valorLinhaComAdicionais, resolverProdutoDaLinha } from './useConverterPropostaEmItens';
import { linhaPertenceAItem, telefonesDoItem } from '@/lib/linhasProtegidasRevisao';
import { linhaFicaCancelada } from '@/lib/ficaCanceladaRevisao';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { ComposicaoProposta, LinhaComposicao } from '@/constants/planosCNPJ';
import { CATEGORIA_M2M, conferirServicoNaLinha, linhaEhServicoDigital, mensagemProblemaServico } from '@/lib/servicoNaLinha';
import { CAMPOS_OPERACIONAIS } from '@/constants/camposEstruturais';
import { separarLinhasPorFamilia } from '@/lib/familiasProduto';
import { criarPedidosPorFamilia } from './useCriarPedidosPorFamilia';
import { conferirPropostaAtual } from '@/lib/conferirPropostaAtual';
import { blocoRenovSemRenovacao } from '../../../backend/src/services/propostas/bloco-renov';

interface CamposOperacionais {
  ddd?: string | null;
  linha?: string | null;
  tipo_chip?: string | null;
  operadora_doadora?: string | null;
  linhas_telefone?: any[] | null;  // ✅ Array completo de linhas telefônicas
  gestor_id?: string | null;
  cessionario_id?: string | null;
  endereco_instalacao?: any;
  observacoes_item?: string | null;
  status_id?: string | null;
  quadro_atual?: string | null;
  coluna_atual?: string | null;
  numero_pedido_operadora?: string | null;
  data_ativacao?: string | null;
  data_instalacao?: string | null;
}

/** Recuperar parte do pedido (Douglas 23/10/2026). */
export interface OpcoesRecuperacao {
  /** Itens cancelados que o cliente não quer mais: ficam no cancelado. */
  ficamCancelados?: Iterable<string>;
  /** Motivo novo desses itens; null mantém o motivo do cancelamento. */
  motivoFicamId?: string | null;
}

interface RegeneracaoResultado {
  success: boolean;
  itensRegenerados: number;
  operacionaisPreservados: number;
  error?: string;
}

export function useRegenerarItensPedido() {
  const [regenerando, setRegenerando] = useState(false);
  const { converter } = useConverterPropostaEmItens();
  const queryClient = useQueryClient();

  const regenerar = useCallback(async (
    pedidoId: string,
    leadId: string,
    userId: string,
    flushAllFn?: () => Promise<{ success: boolean; errors: string[] }>,
    opcoes?: OpcoesRecuperacao,
  ): Promise<RegeneracaoResultado> => {
    setRegenerando(true);
    
    try {
      // ✅ STEP 0: FLUSH TODOS OS SALVAMENTOS PENDENTES
      if (flushAllFn) {
        console.log('🔄 [REGENERAR] Forçando flush de salvamentos pendentes...');
        const flushResult = await flushAllFn();
        
        if (!flushResult.success) {
          throw new Error(
            `Não foi possível salvar todos os dados antes de regenerar. ` +
            `Erros: ${flushResult.errors.join(', ')}`
          );
        }
        
        // Aguardar propagação
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      console.log('🔄 [REGENERAR] Iniciando regeneração segura...', { pedidoId, leadId });
      // Revisão não exige reenviar (Douglas 08/10/2026): a primeira proposta enviada
      // fica salva e o cliente recebe a confirmação do pedido em PEDIDO FECHADO /
      // PEDIDO RECUPERADO. A conferência só registra na timeline o que mudou.
      await conferirPropostaAtual(leadId, pedidoId).catch((e) =>
        console.warn('[REGENERAR] conferência da proposta não registrada', e));
      
      // 0.5. Verificar se é revisão de cancelado (antes da checagem de quadro)
      const { data: leadFlagCheck } = await supabase
        .from('leads_carteira')
        .select('revisao_de_cancelado')
        .eq('id', leadId)
        .single();
      
      const isRevisaoCancelado = leadFlagCheck?.revisao_de_cancelado === true;
      const ficam = new Set<string>(isRevisaoCancelado ? [...(opcoes?.ficamCancelados ?? [])] : []);

      // 1. Verificar se algum item do pedido NÃO está em COMERCIAL
      // Exceção: se for revisão de cancelado, itens estão em PEDIDO_CANCELADO (OK)
      if (!isRevisaoCancelado) {
        const { data: itensCheck, error: errCheck } = await supabase
          .from('itens_pedido')
          .select('quadro_atual')
          .eq('pedido_id', pedidoId)
          .neq('quadro_atual', 'COMERCIAL')
          .limit(1);

        if (errCheck) {
          throw new Error('Erro ao verificar quadro do pedido');
        }

        if (itensCheck && itensCheck.length > 0) {
          throw new Error(`Regeneração só permitida quando todos os itens estão em COMERCIAL`);
        }
      }

      // 2. Buscar itens atuais ANTES de qualquer operação (para fallback)
      // Agregar MÃE + FILHOS: os itens migram pro pedido filho quando fecha,
      // então ler só da mãe devolve 0 itens e a proteção (itensProtegidos/
      // protegidosLinhaIds) não enxerga os finalizados. (Douglas 16/06)
      const { data: filhosRegen } = await supabase
        .from('pedidos')
        .select('id')
        .eq('pedido_mae_id', pedidoId);
      const idsRegen = [pedidoId, ...((filhosRegen || []).map((f: any) => f.id))];
      const { data: itensAntigos, error: errItens } = await supabase
        .from('itens_pedido')
        .select('*')
        .in('pedido_id', idsRegen);

      if (errItens) {
        throw new Error('Erro ao buscar itens atuais');
      }

      // Recuperar um pedido sem item cancelado não recupera nada: o toast dizia
      // "foi para PEDIDO RECUPERADO" e a tela abria outro pedido do cliente
      // (pedido 2610020028, 13/10/2026: a fibra cancelada e a revisão no das PN).
      if (isRevisaoCancelado && !(itensAntigos || []).some((i) => i.quadro_atual === 'PEDIDO_CANCELADO')) {
        throw new Error('Este pedido não tem item cancelado para recuperar. Feche esta revisão e clique em Revisar Proposta no próprio pedido cancelado.');
      }

      console.log('📦 [REGENERAR] Itens antigos backup:', itensAntigos?.length || 0);

      // 🛡️ PROTECAO ENTRE FAMILIAS (25/06): a composicao do lead tem linhas de TODOS os
      // pedidos dele (familia movel + fixa). Carrega os itens dos OUTROS pedidos do lead
      // pra NUNCA inserir/recuperar aqui uma linha que ja vive em outro pedido — ex.:
      // revisar a FIBRA cancelada nao pode tocar nas TT POS que estao em CONTRATO_PRIME
      // no pedido movel (senao viravam item duplicado na FIBRA).
      const { data: pedidosDoLead } = await supabase
        .from('pedidos')
        .select('id')
        .eq('lead_origem_id', leadId)
        .eq('deletado', false);
      const idsOutros = (pedidosDoLead || [])
        .map((p: any) => p.id)
        .filter((id: string) => !idsRegen.includes(id));
      const { data: itensOutrosPedidos } = idsOutros.length > 0
          ? await supabase
            .from('itens_pedido')
            .select('id, pedido_id, linha_id, linhas_telefone, quadro_atual, familia')
            .in('pedido_id', idsOutros)
        : { data: [] as any[] };
      if ((itensOutrosPedidos || []).length > 0) {
        console.log('🛡️ [REGENERAR] Itens de outros pedidos do lead (protegidos):', itensOutrosPedidos!.length);
      }

      // 2.5 🔒 LEI (Douglas 15/06): trabalha-se por ITEM, não por pedido.
      // - Na revisão de CANCELADO, SÓ os itens cancelados (quadro PEDIDO_CANCELADO)
      //   podem ser mexidos. TUDO que não está cancelado (em andamento, finalizado)
      //   é PROTEGIDO: nunca apagado nem alterado, fica visível e no mesmo status.
      //   (Caso real 12/06: fibra FINALIZADA 100% foi deletada pela revisão de um
      //   cancelado; 15/06: itens em andamento sumiam. Agora só o cancelado entra.)
      // - Fora da revisão de cancelado, mantém a proteção antiga (finalizado/instalado).
      const itensProtegidos = (itensAntigos || []).filter((i: any) =>
        isRevisaoCancelado
          ? i.quadro_atual !== 'PEDIDO_CANCELADO'
          : (i.quadro_atual === 'QUALIDADE' || i.data_ativacao != null)
      );
      const protegidosIds = new Set<string>(itensProtegidos.map((i: any) => i.id));
      const protegidosLinhaIds = new Set<string>(
        itensProtegidos.map((i: any) => i.linha_id).filter(Boolean)
      );
      const itensApagaveis = (itensAntigos || []).filter((i: any) => !protegidosIds.has(i.id));
      // (Douglas 16/06) A regeneração NÃO usa mais delete+recria: itens que já
      // existem são recuperados via UPDATE-no-lugar e só linhas novas viram INSERT.
      // O antigo gate temItemBloqueador deixou de ser necessário.
      // Preserva o NÚMERO (seq) dos itens cancelados ao recriar — "mesmo número de item".
      const seqPorLinhaId = new Map<string, number>();
      if (isRevisaoCancelado) {
        for (const i of itensApagaveis) {
          if (i.linha_id != null && i.seq != null) seqPorLinhaId.set(i.linha_id, i.seq);
        }
      }
      if (itensProtegidos.length > 0) {
        console.log(
          isRevisaoCancelado
            ? `🔒 [REGENERAR] Itens NÃO-cancelados preservados (intocados): ${itensProtegidos.length}`
            : `🔒 [REGENERAR] Itens finalizados/instalados preservados: ${itensProtegidos.length}`
        );
      }

      console.log('🔎 [RECOVER] estado-itens', JSON.stringify({
        pedidoId, isRevisaoCancelado, idsRegen,
        itensAntigos: (itensAntigos||[]).map((i)=>({id:i.id,q:i.quadro_atual,lid:i.linha_id,seq:i.seq})),
        protegidos: [...protegidosIds],
        apagaveis: itensApagaveis.map((i)=>({id:i.id,q:i.quadro_atual})),
      }));
      // 3. Criar mapa de operacionais por linha_id
      const mapaOperacionais = new Map<string, CamposOperacionais>();
      (itensAntigos || []).forEach((item: any) => {
        if (item.linha_id) {
          mapaOperacionais.set(item.linha_id, {
            ddd: item.ddd,
            linha: item.linha,
            tipo_chip: item.tipo_chip,
            operadora_doadora: item.operadora_doadora,
            linhas_telefone: item.linhas_telefone,
            gestor_id: item.gestor_id,
            cessionario_id: item.cessionario_id,
            endereco_instalacao: item.endereco_instalacao,
            observacoes_item: item.observacoes_item,
            // ✅ Se for revisão de cancelado, NÃO preservar posição do kanban anterior
            status_id: isRevisaoCancelado ? null : item.status_id,
            quadro_atual: isRevisaoCancelado ? null : item.quadro_atual,
            coluna_atual: isRevisaoCancelado ? null : item.coluna_atual,
            numero_pedido_operadora: item.numero_pedido_operadora,
            data_ativacao: item.data_ativacao,
            data_instalacao: item.data_instalacao,
          });
        }
      });

      console.log('🗺️ [REGENERAR] Mapa de operacionais criado:', mapaOperacionais.size, 'entradas');

      // 4. Buscar cliente_id do pedido para fallback de gestor/cedente
      const { data: pedidoData } = await supabase
        .from('pedidos')
        .select('cliente_id, consultor_id, origem_lead')
        .eq('id', pedidoId)
        .single();

      let gestorIdFallback: string | null = null;
      let cessionarioIdFallback: string | null = null;

      if (pedidoData?.cliente_id) {
        // 4.1 GESTOR: da tabela cliente_contato
        const { data: contatos } = await supabase
          .from('cliente_contato')
          .select('id, cargo, is_principal, ordem')
          .eq('cliente_id', pedidoData.cliente_id)
          .order('ordem', { ascending: true });

        if (contatos && contatos.length > 0) {
          // Prioridade: cargo gestor/responsável/diretor → is_principal/ordem=0 → primeiro
          const gestorPorCargo = contatos.find(c => 
            /gestor|respons|diret|propriet|sócio|socio/i.test(c.cargo || '')
          );
          const gestorPrincipal = contatos.find(c => c.is_principal || c.ordem === 0);
          gestorIdFallback = gestorPorCargo?.id || gestorPrincipal?.id || contatos[0]?.id || null;
        }

        // 4.2 CEDENTE: da tabela cliente_cessionario (tabela separada!)
        const { data: cessionarios } = await supabase
          .from('cliente_cessionario')
          .select('id, ordem')
          .eq('cliente_id', pedidoData.cliente_id)
          .order('ordem', { ascending: true });

        if (cessionarios && cessionarios.length > 0) {
          // Prioridade: ordem = 0 → primeiro disponível
          cessionarioIdFallback = cessionarios.find(c => c.ordem === 0)?.id || cessionarios[0]?.id || null;
        }
      }

      console.log('👤 [REGENERAR] Fallback gestor:', gestorIdFallback);
      console.log('🏛️ [REGENERAR] Fallback cessionário:', cessionarioIdFallback);

      // 5. Buscar proposta atualizada do lead
      const { data: lead, error: errLead } = await supabase
        .from('leads_carteira')
        .select('proposta_composicao_cnpj')
        .eq('id', leadId)
        .single();

      if (errLead || !lead?.proposta_composicao_cnpj) {
        throw new Error('Proposta não encontrada no lead');
      }

      const composicao = lead.proposta_composicao_cnpj as unknown as ComposicaoProposta;
      
      // 5. PRÉ-VALIDAÇÃO: Verificar se a proposta tem linhas válidas
      if (!composicao.linhas || composicao.linhas.length === 0) {
        throw new Error('Proposta não contém linhas válidas. Nada foi alterado.');
      }
      const semRenovacao = blocoRenovSemRenovacao(composicao.linhas);
      if (semRenovacao) throw new Error(`${semRenovacao} Nada foi alterado.`);

      // 5.1 ✅ (E) VALIDAÇÃO CRÍTICA: Todas as linhas devem ter linha_id
      // EXCEÇÃO: Se pedido não tem itens (0 itens), não precisa de linha_id para merge — não há operacionais a preservar
      const temItensAntigos = (itensAntigos?.length || 0) > 0;
      const linhasSemId = composicao.linhas.filter(l => !l.linha_id);
      if (linhasSemId.length > 0 && temItensAntigos) {
        throw new Error(
          `Proposta contém ${linhasSemId.length} linha(s) sem linha_id. ` +
          `Regeneração abortada para evitar perda de dados operacionais. ` +
          `Salve a proposta novamente para gerar os IDs e tente de novo.`
        );
      } else if (linhasSemId.length > 0 && !temItensAntigos) {
        console.warn(`⚠️ [REGENERAR] ${linhasSemId.length} linhas sem linha_id, mas pedido está vazio — prosseguindo sem guardrail de merge`);
      }

      console.log('✅ [REGENERAR] Proposta válida com', composicao.linhas.length, 'linhas (todas com linha_id)');

      let linhasAlvoPedido = composicao.linhas;
      const { fixaBasica, movel } = separarLinhasPorFamilia(composicao.linhas);
      const familiasAtuais = new Set(
        (itensAntigos || [])
          .map((item) => item.familia)
          .filter((familia: unknown) => familia === 'Fixa Básica' || familia === 'Móvel')
      );
      const familiaAtualUnica = familiasAtuais.size === 1 ? [...familiasAtuais][0] : null;

      if (fixaBasica.length > 0 && movel.length > 0 && familiaAtualUnica && pedidoData?.cliente_id) {
        const linhasNovoPedido = familiaAtualUnica === 'Fixa Básica' ? movel : fixaBasica;
        linhasAlvoPedido = familiaAtualUnica === 'Fixa Básica' ? fixaBasica : movel;
        const familiaComplementar = familiaAtualUnica === 'Fixa Básica' ? 'Móvel' : 'Fixa Básica';
        const itensComplementaresExistentes = (itensOutrosPedidos || []).filter(
          (item) => item.familia === familiaComplementar
        );
        const linhasComplementaresNovas = linhasNovoPedido.filter(
          (linha) => !(itensOutrosPedidos || []).some((item) => linhaPertenceAItem(linha, item))
        );

        if (linhasComplementaresNovas.length > 0 && itensComplementaresExistentes.length > 0) {
          const pedidoComplementarId = itensComplementaresExistentes[0].pedido_id;
          const resultadoComplementar = await converter({
            pedidoId: pedidoComplementarId,
            composicao: { ...composicao, linhas: linhasComplementaresNovas },
            gestorId: gestorIdFallback,
          });
          if (!resultadoComplementar.success || resultadoComplementar.itensCreados === 0) {
            throw new Error(`Não foi possível atualizar o pedido ${familiaComplementar}: ${resultadoComplementar.errors.join(', ') || 'nenhum item criado'}`);
          }
        } else if (linhasComplementaresNovas.length > 0) {
          const resultadoDivisao = await criarPedidosPorFamilia({
            clienteId: pedidoData.cliente_id,
            consultorId: pedidoData.consultor_id || userId,
            origemLead: pedidoData.origem_lead || 'MANUAL',
            composicao: { ...composicao, linhas: linhasComplementaresNovas },
            gestorId: gestorIdFallback,
            leadId,
          });

          if (!resultadoDivisao.success || resultadoDivisao.totalItens === 0) {
            throw new Error(`Não foi possível separar os produtos FIXA e MÓVEL em pedidos diferentes: ${resultadoDivisao.errors.join(', ') || 'pedido complementar sem itens'}`);
          }

          console.log('🔀 [REGENERAR] Família complementar separada em novo pedido:', {
            familiaMantida: familiaAtualUnica,
            pedidosCriados: resultadoDivisao.pedidos.map((p) => p.numero_pedido),
          });
        }
      }

      // 5.2 🔒 Linhas da proposta que correspondem a itens protegidos NÃO são
      // recriadas — o item finalizado original permanece exatamente como está.
      let linhasParaRegenerar = linhasAlvoPedido.filter(
        l => (!l.linha_id || !protegidosLinhaIds.has(l.linha_id))
          && !(itensOutrosPedidos || []).some((i: any) => linhaPertenceAItem(l, i))
      );

      if (isRevisaoCancelado) {
        const digitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');
        const itensSemLinha = (itensApagaveis as any[]).filter((item: any) =>
          item.quadro_atual === 'PEDIDO_CANCELADO'
          && !ficam.has(item.id)
          && item.linha_id
          && telefonesDoItem(item).length > 0
          && !linhasParaRegenerar.some((l: any) => linhaPertenceAItem(l, item))
        );
        const usados = new Set<string>();
        const trocadas = new Map<LinhaComposicao, LinhaComposicao>();
        for (const linha of linhasParaRegenerar as any[]) {
          if (linha.tipo === 'SERVICO' || digitos(linha.numero).length < 10) continue;
          if ((itensAntigos || []).some((i: any) => linhaPertenceAItem(linha, i))) continue;
          const produto = await resolverProdutoDaLinha(linha);
          const candidatos = itensSemLinha.filter((item: any) =>
            !usados.has(item.id) && produto && item.produto_id === produto
          );
          if (candidatos.length !== 1) continue;
          usados.add(candidatos[0].id);
          trocadas.set(linha, { ...linha, linha_id: candidatos[0].linha_id });
        }
        if (trocadas.size > 0) {
          linhasParaRegenerar = linhasParaRegenerar.map((l) => trocadas.get(l) ?? l);
        }
      }
      // Serviço digital é 1 item por licença, como no conversor (Douglas 26/10/2026:
      // "de cada linha um produto"). O card com Lic. N vira N licenças de 1.
      // O M2M não se abre: é serviço único, as linhas são a quantidade de um item só.
      linhasParaRegenerar = linhasParaRegenerar.flatMap((l) => {
        const q = l.tipo === 'SERVICO' && l.categoriaCustom !== CATEGORIA_M2M ? Math.max(1, Math.floor(Number(l.quantidade) || 1)) : 1;
        return q > 1 ? Array.from({ length: q }, () => ({ ...l, quantidade: 1 })) : [l];
      });
      // Serviço digital só pela linha, um de cada por linha (Douglas 26/10/2026).
      // Confere o que vai entrar ou voltar; o que fica cancelado e o item protegido não.
      const servicosQueEntram = linhasParaRegenerar.filter((l) => {
        if (!linhaEhServicoDigital(l)) return false;
        const item = (itensAntigos || []).find((i: any) => l.linha_id && i.linha_id === l.linha_id);
        return !(item && ficam.has(item.id));
      });
      const problemaServico = mensagemProblemaServico(conferirServicoNaLinha([
        ...composicao.linhas.filter((l) => l.tipo !== 'SERVICO'),
        ...servicosQueEntram,
      ]));
      if (problemaServico) throw new Error(`${problemaServico} Nada foi alterado.`);
      console.log('🔎 [RECOVER] estado-linhas', JSON.stringify({
        propostaLinhas: composicao.linhas.map((l)=>({lid:l.linha_id,tipo:l.tipo,num:l.numero})),
        linhasSemId: linhasSemId.length,
        linhasParaRegenerar: linhasParaRegenerar.length,
        temItensAntigos,
      }));
      if (linhasParaRegenerar.length === 0 && !isRevisaoCancelado) {
        throw new Error('Todos os itens deste pedido já estão finalizados/instalados — nada para regenerar.');
      }

      // 🔒 LEI (Douglas 16/06): regeneração NUNCA apaga item de itens_pedido.
      // Em vez de delete+recria, fazemos UPDATE-no-lugar nos itens que já existem
      // (preservando DDD/linha/plano/operacionais) e INSERT só nas linhas realmente
      // novas. Item que sumiu da proposta fica como está (não é apagado).
      //   - itensParaRecuperar = itens apagáveis (cancelados) que TÊM linha
      //     correspondente na proposta. Recuperados via UPDATE.
      //   - linhasNovas = linhas da proposta SEM item existente. Criadas via INSERT.
      // (Douglas 16/06) Casamento por linha_id OU TELEFONE (linhaPertenceAItem):
      // ao trocar o plano na revisão, o editor gera a linha com linha_id NOVO; sem
      // casar por telefone o item cancelado velho não era reusado e a linha nova
      // virava INSERT → pedido com DUPLICATA (mesmo telefone, plano velho + novo).
      // Casar por telefone faz o telefone que já tem item cancelado RECUPERAR aquele
      // item; só telefone genuinamente novo vira INSERT.
      // Recuperar parte do pedido (Douglas 23/10/2026): volta TUDO o que está
      // cancelado e tem linha na proposta, menos o que o consultor marcou como
      // "Fica cancelada". Até aqui voltava só o item alterado (11/09, pedido
      // P2608120032: a PN que devia ficar voltou junto) e a linha intocada ficava
      // cancelada sem a tela dizer; agora a escolha é explícita, linha a linha, e
      // o cartão Recuperar Pedido mostra antes o que volta e o que fica.
      // O serviço/aparelho segue a linha dona.
      const normalizarTelefone = (v: unknown) =>
        String(v ?? '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
      const idsItensQueVoltam = new Set<string>();
      const idsQueFicam = new Set<string>();
      const linhaPaiPorItemId = new Map<string, string>();

      if (isRevisaoCancelado) {
        const idsDeLinha = new Set<string>();
        for (const item of itensApagaveis as any[]) {
          if (item.quadro_atual !== 'PEDIDO_CANCELADO') continue;
          const linha = linhasParaRegenerar.find((l: any) => l.tipo !== 'SERVICO' && linhaPertenceAItem(l, item));
          if (!linha) continue;
          idsDeLinha.add(item.id);
          // Passaport e aparelho carregam o telefone da linha: casam com ela aqui e
          // ficam junto quando a linha fica.
          const fica = ficam.has(item.id) || linhaFicaCancelada(linha, linhasParaRegenerar, itensAntigos || [], ficam);
          (fica ? idsQueFicam : idsItensQueVoltam).add(item.id);
        }

        // A linha de serviço/aparelho tem identidade própria, mas pertence à
        // linha móvel por linhaVinculadaId (preferencial) ou pelo telefone legado.
        for (const linha of linhasParaRegenerar as any[]) {
          if (linha.tipo !== 'SERVICO') continue;
          const telefoneVinculado = normalizarTelefone(linha.linhaVinculada);
          const cancelados = (itensApagaveis as any[]).filter((i: any) => i.quadro_atual === 'PEDIDO_CANCELADO');
          const item = cancelados.find((i: any) => linhaPertenceAItem(linha, i))
            ?? (telefoneVinculado.length >= 10
              ? cancelados.find((i: any) => !idsDeLinha.has(i.id)
                && telefonesDoItem(i).some((tel: string) => normalizarTelefone(tel) === telefoneVinculado))
              : undefined);
          if (!item) continue;
          if (ficam.has(item.id) || linhaFicaCancelada(linha, linhasParaRegenerar, itensAntigos || [], ficam)) {
            idsItensQueVoltam.delete(item.id);
            idsQueFicam.add(item.id);
            continue;
          }
          if (idsQueFicam.has(item.id)) continue;
          idsItensQueVoltam.add(item.id);
          if (linha.linhaVinculadaId) linhaPaiPorItemId.set(item.id, linha.linhaVinculadaId);
        }

        for (const item of itensApagaveis as any[]) {
          if (item.quadro_atual === 'PEDIDO_CANCELADO' && ficam.has(item.id)) idsQueFicam.add(item.id);
        }
      }

      const itensParaRecuperarCandidatos = itensApagaveis.filter(
        (i: any) => isRevisaoCancelado
          ? idsItensQueVoltam.has(i.id)
          : linhasParaRegenerar.some((l: any) => linhaPertenceAItem(l, i))
      );
      // 🚨 (Douglas 24/08, pedido #P2608190040 relatado pelo supervisor Filipe)
      // LINHA SEM TELEFONE PRECISA DE IDENTIDADE, SENÃO A REVISÃO DUPLICA.
      //
      // `linhaPertenceAItem` casa por linha_id OU telefone — e desiste na hora
      // quando o item não tem telefone. Só que o editor gera linha_id NOVO ao
      // trocar o plano. Resultado: bloco de dados e adicional (Passaport) ficam
      // sem nada com que casar, viram "linha nova" e são INSERIDOS ao lado dos
      // antigos. Como a revisão nunca apaga (LEI 16/06), cada revisão SOMA.
      //
      // Pior no bloco: o conversor junta N linhas num item só e guarda o
      // linha_id de UMA. Na revisão 1 casou e 11 não → item novo de 11.
      // 12 = 1 + 11, e a diferença batia no centavo com o total errado da tela.
      //
      // A régua que faltava: **um item sem telefone representa TODAS as linhas
      // do plano dele naquele pedido.** É o que o conversor já faz ao agregar —
      // só não estava escrito do lado da revisão.
      type ItemAgregavel = {
        id: string;
        produto_id?: string | null;
        plano_nome?: string | null;
        quantidade?: number | string | null;
        quadro_atual?: string | null;
        created_at?: string | null;
        linha_id?: string | null;
      };
      const chavePlano = (s: unknown) =>
        String(s ?? '')
          .trim()
          .toLowerCase()
          .replace(/^(linha nova|bloco de dados(?:\s*\([^)]*\))?)\s*-\s*/i, '')
          .replace(/\s+/g, ' ');
      // ⛔ ACOMPANHAMENTO NÃO É ITEM AGREGADO (Douglas 01/09/2026).
      // Aparelho e Passaport nascem com `linha_id: null` e não preenchem
      // `linhas_telefone` — caíam aqui como "item agregado sem telefone". Só que
      // nenhum deles é LINHA da proposta, então a chave deles nunca entrava em
      // `chavesAgregadasDesejadas` e a limpeza os APAGAVA a cada "Salvar e
      // Aplicar ao Pedido": o pedido perdia o aparelho e o roaming em silêncio,
      // com o faturamento caindo junto. A limpeza só pode alcançar item que
      // ALGUMA linha sem telefone poderia ter gerado (bloco, fibra, TV, serviço).
      const ehAcompanhamento = (i: ItemAgregavel) => {
        const obs = String((i as { observacoes_item?: string | null }).observacoes_item ?? '');
        if (/^\s*Vinculado à linha/i.test(obs)) return true;      // Passaport
        if (/^\s*Aparelho\s/i.test(obs)) return true;             // Aparelho
        // Mesh é adicional da Fibra na proposta e item próprio no pedido.
        if (/^Mesh separado da proposta; item principal /i.test(obs)
          || (Boolean((i as { linha_pai_id?: string | null }).linha_pai_id) && i.plano_nome === 'Wi-Fi Mesh')) return true;
        return false;
      };
      const itensSemTelefone: ItemAgregavel[] = (itensAntigos || []).filter(
        (i: ItemAgregavel) => telefonesDoItem(i).length === 0
          && chavePlano(i.plano_nome) !== ''
          && !ehAcompanhamento(i)
      ).sort((a: ItemAgregavel, b: ItemAgregavel) =>
        String(a.created_at || '').localeCompare(String(b.created_at || ''))
      );
      // A linha vinculada entra na IDENTIDADE do item agregado, como já entra na
      // conversão (`categoriaCustom::planoId::linhaVinculada`). Sem ela, duas
      // licenças do mesmo serviço em telefones diferentes viravam uma só na
      // revisão — a segunda era apagada, levando o telefone dela junto.
      // Só DDD não é telefone: a linha nova guarda "51" no item e nada na
      // proposta, e a diferença fazia as 5 linhas novas não casarem com o item
      // de 4 (pedido #P2610190064, 20/10/2026).
      const soDigitosTel = (v: unknown) => {
        const d = String(v ?? '').replace(/\D/g, '');
        return d.length >= 8 ? d : '';
      };
      const telDoItemAgregado = (item: ItemAgregavel) => {
        const it = item as { ddd?: string | null; linha?: string | null };
        return soDigitosTel(`${it.ddd ?? ''}${it.linha ?? ''}`);
      };
      const chaveItemAgregado = (item: ItemAgregavel) =>
        `${item.produto_id || 'sem-produto'}::${chavePlano(item.plano_nome)}::${telDoItemAgregado(item)}`;
      const chaveLinhaAgregada = (linha: LinhaComposicao) => {
        const plano = resolverPlano(linha);
        return `${plano?.produtoPedidoId || 'sem-produto'}::${chavePlano(plano?.nome)}::${soDigitosTel(linha.linhaVinculada)}`;
      };
      const linhaIdsDaProposta = new Set(
        linhasParaRegenerar.map((l: LinhaComposicao) => l.linha_id).filter((id): id is string => Boolean(id)),
      );
      // Cada licença fica com um item só: duas licenças nunca casam com o mesmo.
      const licencaAtribuida = new Map<LinhaComposicao, ItemAgregavel | null>();
      const itensDeLicencaUsados = new Set<string>();
      /** Item agregado (sem telefone) que já responde por esta linha. */
      const itemAgregadoDaLinha = (l: LinhaComposicao): ItemAgregavel | null => {
        if (l.tipo !== 'SERVICO') return itemAgregadoDaLinhaBase(l, () => true);
        if (licencaAtribuida.has(l)) return licencaAtribuida.get(l) ?? null;
        const it = itemAgregadoDaLinhaBase(l, (i) => !itensDeLicencaUsados.has(i.id));
        if (it) itensDeLicencaUsados.add(it.id);
        licencaAtribuida.set(l, it);
        return it;
      };
      function itemAgregadoDaLinhaBase(l: LinhaComposicao, livre: (i: ItemAgregavel) => boolean): ItemAgregavel | null {
        if (String(l?.numero ?? '').replace(/\D/g, '').length >= 8) return null; // tem telefone: não é agregado
        const chave = chaveLinhaAgregada(l);
        const nome = chavePlano(resolverPlano(l)?.nome);
        if (!nome) return null;
        // Licença de serviço é 1 item por licença (conversor). Casar pela chave
        // jogava as 7 linhas de Claro Monitor no 1º item, que ia a quantidade 7
        // (#P2610230051, 26/10/2026). O item da própria linha vence; os itens de
        // outras licenças não servem a esta.
        const proprio = l.linha_id ? itensSemTelefone.find((i) => i.linha_id === l.linha_id && livre(i)) : undefined;
        if (proprio) return proprio;
        const candidatos = l.tipo === 'SERVICO'
          ? itensSemTelefone.filter((i) => livre(i) && (!i.linha_id || !linhaIdsDaProposta.has(i.linha_id)))
          : itensSemTelefone;
        const exato = candidatos.find((i) => chaveItemAgregado(i) === chave);
        if (exato) return exato;
        const telDaLinha = soDigitosTel(l.linhaVinculada);
        const porNome = candidatos.filter((i) => {
          if (chavePlano(i.plano_nome) !== nome) return false;
          return telDoItemAgregado(i) === telDaLinha;
        });
        const produtos = new Set(porNome.map((i) => i.produto_id || 'sem-produto'));
        return produtos.size === 1 ? porNome[0] ?? null : null;
      }

      // linhasNovas compara contra TODOS os itens antigos (ativos OU cancelados):
      // assim, linha cujo telefone já vive num item (inclusive protegido) não é
      // re-inserida como duplicata.
      const linhasNovas = linhasParaRegenerar.filter(
        (l: any) =>
          !(itensAntigos || []).some((i: any) => linhaPertenceAItem(l, i))
          && !itemAgregadoDaLinha(l)
      );

      // Quantas linhas da proposta cada item agregado responde AGORA. Sem isto,
      // matar a duplicata trocaria "total alto demais" por "total baixo demais":
      // o cliente que passa de 12 para 15 blocos ficaria com 12 em silêncio.
      const qtdPorItemAgregado = new Map<string, number>();
      for (const l of linhasParaRegenerar) {
        const it = itemAgregadoDaLinha(l);
        const quantidade = l.tipo === 'SERVICO'
          ? Math.max(1, Math.floor(Number(l.quantidade) || 1))
          : 1;
        if (it) qtdPorItemAgregado.set(it.id, (qtdPorItemAgregado.get(it.id) ?? 0) + quantidade);
      }

      const chavesAgregadasDesejadas = new Set<string>();
      for (const linha of linhasParaRegenerar) {
        if (String(linha.numero ?? '').replace(/\D/g, '').length >= 8) continue;
        const existente = itemAgregadoDaLinha(linha);
        chavesAgregadasDesejadas.add(existente ? chaveItemAgregado(existente) : chaveLinhaAgregada(linha));
      }
      const linhaIdsServico = new Set(
        linhasParaRegenerar.filter((l) => l.tipo === 'SERVICO' && l.linha_id).map((l) => l.linha_id as string),
      );
      const primeiroItemPorChave = new Map<string, string>();
      const itensAgregadosRemover = itensSemTelefone.filter((item) => {
        if (isRevisaoCancelado || item.quadro_atual !== 'COMERCIAL') return false;
        if (qtdPorItemAgregado.has(item.id)) return false;
        // Licença a mais do mesmo card (Lic. 4 → 2): sobra sem licença na proposta.
        if (item.linha_id && linhaIdsServico.has(item.linha_id)) return true;
        const chave = chaveItemAgregado(item);
        if (!chavesAgregadasDesejadas.has(chave)) return true;
        if (!primeiroItemPorChave.has(chave)) {
          primeiroItemPorChave.set(chave, item.id);
          return false;
        }
        return true;
      });
      const idsAgregadosRemovidos = new Set(itensAgregadosRemover.map((item) => item.id));
      const idsAgregadosMantidos = new Set(qtdPorItemAgregado.keys());
      // ⛔ Passaport que saiu da proposta não se recupera nem fica (Douglas 09/10/2026,
      // #P2610080041: proposta com 1 Passaport Mundo, pedido com América + 2 Mundo).
      const passaportFora = await itensPassaportForaDaProposta({ pedidoId, composicao });
      if (passaportFora.errors.length > 0) throw new Error(passaportFora.errors.join(' · '));
      const idsPassaportFora = new Set(
        passaportFora.itens.filter((i) => !protegidosIds.has(i.id)).map((i) => i.id),
      );
      const itensParaRecuperar = (itensAntigos || []).filter(
        (item: ItemAgregavel) =>
          !idsPassaportFora.has(item.id)
          && !idsAgregadosRemovidos.has(item.id)
          && (itensParaRecuperarCandidatos.some((candidato: ItemAgregavel) => candidato.id === item.id)
            || (idsAgregadosMantidos.has(item.id)
              && (!isRevisaoCancelado
                || (!ficam.has(item.id)
                  && (item.quadro_atual === 'PEDIDO_CANCELADO'
                    || qtdPorItemAgregado.get(item.id) !== Number(item.quantidade)))))),
      );
      if (isRevisaoCancelado && ficam.size > 0 && itensParaRecuperar.length === 0 && linhasNovas.length === 0) {
        throw new Error('Nenhum item volta para o pedido: todos ficaram cancelados. Deixe ao menos um voltar ou cancele a revisão. Nada foi alterado.');
      }
      console.log('🔎 [RECOVER] separacao-itens', JSON.stringify({
        itensParaRecuperar: itensParaRecuperar.map((i: any) => ({ id: i.id, lid: i.linha_id, q: i.quadro_atual })),
        linhasNovas: linhasNovas.map((l: any) => ({ lid: l.linha_id, tipo: l.tipo, num: l.numero })),
      }));

      // 2. UPDATE-no-lugar (ZERO delete): a revisão REFLETE o produto editado no
      // MESMO item já vinculado ao pedido. O item está na planilha, NUNCA é apagado
      // — só atualizado. (Douglas 22/06: "se editar um produto ele tem que refletir
      // no item do pedido"; o item nao pode ser apagado.)
      //   - plano_nome / valor_unit / franquia_gb vêm do plano resolvido (mesma
      //     fonte do converter: resolverPlano -> linha.plano OU getPlanoById, que já
      //     resolve pelo catálogo do banco) da linha CASADA (por linha_id OU telefone);
      //   - o telefone vem da linha casada da proposta; operadora/tipo de chip
      //     continuam preservados no mesmo item.
      // A trava fn_proteger_campos_estruturais_item bloqueia mudar plano de item fora
      // do COMERCIAL (item cancelado). Por isso usamos a RPC api_aplicar_produto_item_revisao
      // (SECURITY DEFINER), que libera a trava SÓ nesta transação e, no recover, move
      // pra PEDIDO RECUPERADO na mesma operação.
      // Itens AGRUPADOS (RENOVAÇÃO grade / bloco, qtd>1) NÃO são re-precificados aqui
      // (valor é agregado de várias linhas): no cancelado só movem o quadro, como antes.
      let planoNaoResolvido = 0;
      let motivoFalhaPlano: string | null = null;
      for (const item of itensParaRecuperar) {
        const linhaPaiId = linhaPaiPorItemId.get(item.id);
        if (linhaPaiId) {
          const { error: errVinculo } = await supabase
            .from('itens_pedido')
            .update({ linha_pai_id: linhaPaiId })
            .eq('id', item.id);
          if (errVinculo) throw new Error(`Erro ao vincular acompanhamento à linha: ${errVinculo.message}`);
        }
        const itemEhAgregado = idsAgregadosMantidos.has(item.id);
        // ⛔ Passaport e aparelho carregam o telefone da linha que acompanham
        // (desde 12/09) e casavam com ela aqui: a revisão gravava o plano e o
        // preço da LINHA por cima do roaming e o pedido ganhava uma linha em
        // dobro. Acompanhamento não é linha: não se re-precifica pela linha.
        // (Douglas 02/10/2026, pedido #P2610020041)
        const itemAcompanha = ehAcompanhamento(item);
        const linhaCasada: any = itemAcompanha ? null : linhasParaRegenerar.find((l: any) =>
          linhaPertenceAItem(l, item) || (itemEhAgregado && itemAgregadoDaLinha(l)?.id === item.id)
        );
        const planoNovo = linhaCasada ? resolverPlano(linhaCasada) : null;
        // Produto pela mesma cadeia canônica do conversor. Uma troca de tipo na
        // revisão (ex.: PN → HA) altera o MESMO item; não cria duplicata e não
        // deixa o item cancelado antigo ao lado do novo.
        const produtoNovo = linhaCasada ? await resolverProdutoDaLinha(linhaCasada) : null;
        const itemSingle = (item.quantidade ?? 1) === 1;

        // (24/08) ITEM AGREGADO (bloco de dados): ele responde por N linhas da
        // proposta, e agora as N casam com ele em vez de virarem item novo. Só
        // que N pode ter mudado — o cliente passou de 12 blocos para 15, ou para
        // 9. Sem acertar a quantidade aqui, matar a duplicata trocaria "total
        // alto demais" por "total baixo demais", que é igualmente errado e mais
        // difícil de perceber. `subtotal` é coluna GERADA, então o total do
        // pedido se corrige sozinho.
        // ⛔ NÃO re-precifica: valor de item agregado é agregado de várias linhas
        // e continua fora da revisão, como já era.
        const qtdNova = qtdPorItemAgregado.get(item.id);
        if (qtdNova != null && qtdNova !== Number(item.quantidade)) {
          // linhas_telefone acompanha a quantidade: cada linha nova é uma
          // entrada, senão o pedido mostra 5 no total e 4 linhas para ativar.
          type LinhaTel = { ddd?: string | null; linha?: string | null };
          const ltBruto = (item as { linhas_telefone?: unknown }).linhas_telefone;
          const ltItem: LinhaTel[] = Array.isArray(ltBruto) ? ltBruto : [];
          const ltAjustado = ltItem.length > 0 && ltItem.length !== qtdNova
            && ltItem.every((lt) => soDigitosTel(`${lt?.ddd ?? ''}${lt?.linha ?? ''}`) === '')
            ? (ltItem.length > qtdNova
                ? ltItem.slice(0, qtdNova)
                : [...ltItem, ...Array.from({ length: qtdNova - ltItem.length }, () => ({ ...ltItem[ltItem.length - 1] }))])
            : null;
          const { error: errQtd } = await supabase.rpc('api_aplicar_produto_item_revisao', {
            p_item_id: item.id,
            p_produto_id: null,        // null = mantém
            p_plano_nome: null,
            p_valor_unit: null,
            p_franquia_gb: null,
            p_linhas_telefone: ltAjustado,
            p_recuperar: isRevisaoCancelado && item.quadro_atual === 'PEDIDO_CANCELADO',
            p_quantidade: qtdNova,
          });
          if (errQtd) {
            throw new Error(`Não foi possível acertar a quantidade do item ${item.plano_nome ?? ''}: ${errQtd.message}`);
          } else {
            console.log('✅ [REGENERAR] Item agregado acertado:', item.id, `${item.quantidade} → ${qtdNova}`);
          }
        }

        // Fibra tem uma instalação por item; sua revisão sincroniza o Mesh.
        if (itemEhAgregado && linhaCasada?.tipo !== 'FIBRA') continue;

        if (planoNovo && itemSingle) {
          const ltAtual = Array.isArray(item.linhas_telefone) ? item.linhas_telefone : [];
          const numeroNovo = String(linhaCasada?.numero ?? '').replace(/\D/g, '');
          const virouLinhaNova = linhaCasada?.tipo === 'LINHA_NOVA';
          const dddNovo = numeroNovo.length >= 2 ? numeroNovo.slice(0, 2) : null;
          // A RPC usa COALESCE; string vazia é intencional para apagar a antiga
          // linha de PN e deixar somente o DDD na HA.
          const linhaNova = virouLinhaNova ? '' : numeroNovo.length >= 10 ? numeroNovo.slice(2) : null;
          const novasLT = ltAtual.length > 0
            ? ltAtual.map((lt: any, index: number) => ({
                ...lt,
                ddd: index === 0 && dddNovo ? dddNovo : lt?.ddd,
                linha: index === 0 && virouLinhaNova ? '' : index === 0 && linhaNova ? linhaNova : lt?.linha,
                operadora_doadora: index === 0 && virouLinhaNova ? null : lt?.operadora_doadora,
                gb: planoNovo.gb != null ? String(planoNovo.gb) : (lt?.gb ?? ''),
              }))
            : null;

          const revisao = {
            p_item_id: item.id,
            p_produto_id: produtoNovo ?? ((planoNovo as any)?.produtoPedidoId ?? null), // null = mantém produto atual
            p_plano_nome: planoNovo.nome ?? null,
            // ⛔ COM os adicionais (Mesh, Claro Fixo, ponto de TV). `planoNovo.valor`
            // e' o plano PURO: usar ele aqui apagava o adicional na revisao, e o
            // pedido ficava mais barato que a proposta. (Eduarda 21/08/2026)
            p_valor_unit: valorLinhaComAdicionais(linhaCasada, planoNovo) || (planoNovo.valor ?? null),
            p_franquia_gb: planoNovo.gb ?? null,
            p_linhas_telefone: novasLT,
            p_recuperar: isRevisaoCancelado,
            p_ddd: dddNovo,
            p_linha: linhaNova,
          };
          const { error: errPlano } = linhaCasada?.tipo === 'FIBRA'
            ? await supabase.rpc('api_revisar_fibra_com_mesh', { p_revisao: revisao, p_adicionais: linhaCasada.adicionais || {} })
            : await supabase.rpc('api_aplicar_produto_item_revisao', revisao);

          if (errPlano) {
            // Item bloqueado (ex.: já em TRAMITAÇÃO na operadora) — não dá pra re-precificar,
            // e isso é CORRETO (a operadora já está processando o plano). NÃO aborta o recover:
            // mantém o plano atual; no cancelado, ao menos recupera o quadro. (Douglas 22/06)
            console.warn('[REGENERAR] Plano não aplicado no item', item.id, '—', errPlano.message);
            planoNaoResolvido++;
            motivoFalhaPlano ??= errPlano.message;
            if (isRevisaoCancelado) {
              const { error: errMover } = await supabase
                .from('itens_pedido')
                .update({
                  quadro_atual: 'CONTRATO_PRIME',
                  coluna_atual: 'PEDIDO RECUPERADO',
                  status_id: 'fbcbdacf-3943-4a65-99a3-9c8102988d75',
                  data_cancelamento: null,
                })
                .eq('id', item.id);
              if (errMover) {
                throw new Error('Erro ao recuperar item cancelado: ' + (errMover.message || JSON.stringify(errMover)));
              }
            }
          }
        } else {
          if (!planoNovo && !itemAcompanha) {
            planoNaoResolvido++;
            motivoFalhaPlano ??= 'o plano escolhido na linha não foi resolvido no catálogo';
          }
          // Sem plano resolvido OU item agrupado: mantém o comportamento antigo
          // (no cancelado, só move o quadro pra PEDIDO RECUPERADO; fora dele, intacto).
          if (isRevisaoCancelado) {
            const { error: errRecuperar } = await supabase
              .from('itens_pedido')
              .update({
                quadro_atual: 'CONTRATO_PRIME',
                coluna_atual: 'PEDIDO RECUPERADO',
                status_id: 'fbcbdacf-3943-4a65-99a3-9c8102988d75',
                data_cancelamento: null,
              })
              .eq('id', item.id);

            if (errRecuperar) {
              throw new Error('Erro ao recuperar itens cancelados: ' + (errRecuperar.message || JSON.stringify(errRecuperar)));
            }
          }
        }
      }
      // O toast de sucesso mentia: a RPC falhava (ex.: constraint de franquia no
      // caso PN→TT de 07/08), o fallback só movia o quadro, e o consultor saía
      // certo de que trocou o plano. Falha de plano agora fala ALTO.
      if (planoNaoResolvido > 0) {
        toast.warning(`O plano novo NÃO foi aplicado em ${planoNaoResolvido} item(ns)`, {
          description: `${motivoFalhaPlano ?? 'motivo não identificado'}. O item seguiu com o plano ANTIGO — confira no pedido e refaça a revisão.`,
          duration: 15000,
        });
      }
      if (itensParaRecuperar.length > 0) {
        console.log(
          '✅ [REGENERAR] Itens existentes atualizados no lugar (produto refletido):',
          itensParaRecuperar.length,
          planoNaoResolvido > 0 ? `(${planoNaoResolvido} sem plano resolvido)` : ''
        );
      }

      // 3. INSERT só das linhas NOVAS (nunca delete). Pós-processamento roda só
      // para os itens recém-criados (os recuperados acima já têm operacionais).
      let novosCriados = 0;
      try {
        if (linhasNovas.length > 0) {
          invalidarCacheConversor(); // Forçar novo fetch de mapeamentos para evitar cache vazio
          const resultado = await converter({
            pedidoId,
            composicao: { ...composicao, linhas: linhasNovas },
            gestorId: gestorIdFallback,      // SEMPRE passar fallback
            cessionarioId: cessionarioIdFallback,
          });

          if (!resultado.success) {
            // NÃO apagamos nada → não há rollback de delete a fazer.
            console.error('❌ [REGENERAR] Erro ao criar itens novos');
            throw new Error(resultado.errors.join(', '));
          }

          novosCriados = resultado.itensCreados;
          console.log('✅ [REGENERAR]', novosCriados, 'itens NOVOS criados');
        } else {
          console.log('ℹ️ [REGENERAR] Nenhuma linha nova — nada a inserir (só UPDATE-no-lugar).');
        }

        // APARELHO na revisão (Douglas 21/08/2026). O aparelho NÃO é linha: é um
        // campo dentro de uma linha que já tem item. Como esta rotina só INSERE
        // "linhas novas", incluir um aparelho numa revisão não criava nada — e em
        // silêncio, porque a proposta mostrava certo. A função é idempotente:
        // pula a linha que já tem item de aparelho no pedido.
        try {
          const rAp = await criarItensAparelhoFaltantes({
            pedidoId,
            composicao,
            gestorId: gestorIdFallback,
          });
          if (rAp.criados > 0) {
            novosCriados += rAp.criados;
            console.log('✅ [REGENERAR]', rAp.criados, 'item(ns) APARELHO criado(s)');
          }
          if (rAp.errors.length > 0) throw new Error(rAp.errors.join(' · '));
        } catch (e) {
          throw new Error(`A revisão não foi aplicada porque o aparelho não pôde ser sincronizado: ${e instanceof Error ? e.message : String(e)}`);
        }

        // PASSAPORT (roaming) pelo mesmo motivo do aparelho: é adicional DENTRO
        // de uma linha que já tem item, então não entra em "linha nova" e não era
        // criado por ninguém — a proposta mostrava o roaming e o pedido não tinha.
        // (Douglas 01/09/2026, pedido #P2609010021.) Idempotente: só cria o que falta.
        try {
          const rPass = await criarItensPassaportFaltantes({
            pedidoId,
            composicao,
            gestorId: gestorIdFallback,
          });
          if (rPass.criados > 0) {
            novosCriados += rPass.criados;
            console.log('✅ [REGENERAR]', rPass.criados, 'item(ns) PASSAPORT criado(s)');
          }
          if (rPass.errors.length > 0) console.warn('[REGENERAR] passaport:', rPass.errors.join(' · '));
        } catch (e) {
          console.warn('[REGENERAR] falha ao criar passaport:', e);
        }

        const passaportForaAgora = await itensPassaportForaDaProposta({ pedidoId, composicao });
        if (passaportForaAgora.errors.length > 0) throw new Error(passaportForaAgora.errors.join(' · '));
        const passaportApagar = passaportForaAgora.itens.filter(
          (i) => !protegidosIds.has(i.id) && i.quadro_atual === 'COMERCIAL',
        );
        if (passaportApagar.length > 0) {
          const { error: errPass } = await supabase
            .from('itens_pedido')
            .delete()
            .in('id', passaportApagar.map((i) => i.id));
          if (errPass) throw new Error(`Não foi possível tirar o Passaport que saiu da proposta: ${errPass.message}`);
        }
        const passaportEmAndamento = passaportForaAgora.itens.filter(
          (i) => i.quadro_atual !== 'PEDIDO_CANCELADO' && !passaportApagar.some((a) => a.id === i.id),
        );
        if (passaportEmAndamento.length > 0) {
          toast.warning('Passaport fora da proposta continua no pedido', {
            description: `${passaportEmAndamento.map((i) => `${i.plano_nome} (${String(i.observacoes_item ?? '').replace(/^Vinculado à linha[^:]*:\s*/i, '')})`).join(', ')} já andou no funil: cancele o item no pedido.`,
          });
        }

        // 8. Buscar SÓ os itens recém-criados (linhas novas) para reaplicar
        // operacionais. Itens recuperados (UPDATE) NÃO entram aqui — já têm tudo.
        const linhaIdsNovas = linhasNovas.map((l: any) => l.linha_id).filter(Boolean);
        const { data: novosItensTodos } = linhaIdsNovas.length > 0
          ? await supabase
              .from('itens_pedido')
              .select('id, linha_id, produto_id, quantidade, observacoes_item, linhas_telefone')
              .eq('pedido_id', pedidoId)
              .in('linha_id', linhaIdsNovas)
          : { data: [] as any[] };
        const novosItens = (novosItensTodos || []).filter((i: any) => !protegidosIds.has(i.id));

        // 10.1 PRÉ-PASSO: Se era revisão de cancelado → mover PRIMEIRO para PEDIDO RECUPERADO
        // (antes de reaplicar operacionais, para que os triggers não bloqueiem campos estruturais)
        if (isRevisaoCancelado && novosItens && novosItens.length > 0) {
          console.log('🔄 [REGENERAR] Revisão de cancelado → movendo itens para PEDIDO RECUPERADO ANTES dos operacionais');
          const { error: errMover } = await supabase
            .from('itens_pedido')
            .update({
              quadro_atual: 'CONTRATO_PRIME',
              coluna_atual: 'PEDIDO RECUPERADO',
              status_id: 'fbcbdacf-3943-4a65-99a3-9c8102988d75',
              data_cancelamento: null,
            })
            .in('id', novosItens.map((i: any) => i.id));
          
          if (errMover) {
            console.error('❌ [REGENERAR] Erro ao mover para PEDIDO RECUPERADO:', errMover);
          } else {
            console.log('✅ [REGENERAR] Itens movidos para PEDIDO RECUPERADO:', novosItens.length);
          }
        }

        // 10.2 🔢 Preservar o NÚMERO do item: devolve o seq antigo (por linha_id)
        // aos itens recuperados — "nunca pode mudar o número do item" (best-effort).
        if (isRevisaoCancelado && seqPorLinhaId.size > 0) {
          for (const item of (novosItens || [])) {
            const seqAntigo = item.linha_id ? seqPorLinhaId.get(item.linha_id) : undefined;
            if (seqAntigo != null) {
              const { error: errSeq } = await supabase
                .from('itens_pedido')
                .update({ seq: seqAntigo })
                .eq('id', item.id);
              if (errSeq) console.warn('⚠️ [REGENERAR] Não preservou seq do item', item.id, errSeq.message);
            }
          }
        }

        let operacionaisReaplicados = 0;

        // 9. Reaplicar operacionais: usar mapa quando existir, senão fallback
        for (const item of (novosItens || [])) {
          const operacionais = item.linha_id ? mapaOperacionais.get(item.linha_id) : null;
          
          // Determinar valores finais: mapa > fallback
          const gestorFinal = operacionais?.gestor_id || gestorIdFallback;
          const cessionarioFinal = operacionais?.cessionario_id || cessionarioIdFallback;
          
          // Só atualizar se tiver algo para aplicar
          const temAlgo = operacionais || gestorFinal || cessionarioFinal;
          
          if (temAlgo) {
            // Para FIBRA/TV (quantidade=1), NÃO sobrescrever linhas_telefone e observacoes_item
            // pois o converter já gera esses campos corretamente
            const isFibraTv = item.quantidade === 1 && item.linhas_telefone && Array.isArray(item.linhas_telefone) && item.linhas_telefone.length === 1;
            
            const { error: errUpdate } = await supabase
              .from('itens_pedido')
              .update({
                ddd: operacionais?.ddd ?? null,
                linha: operacionais?.linha ?? null,
                tipo_chip: operacionais?.tipo_chip ?? null,
                operadora_doadora: (operacionais?.operadora_doadora !== undefined
                  ? operacionais.operadora_doadora
                  : (item.linhas_telefone?.[0]?.operadora_doadora ?? null)) as any,
                // Só reaplicar linhas_telefone/obs do mapa antigo para itens multi-linha (não FIBRA/TV)
                ...(isFibraTv ? {} : {
                  linhas_telefone: operacionais?.linhas_telefone ?? null,
                  observacoes_item: operacionais?.observacoes_item ?? null,
                }),
                gestor_id: gestorFinal,
                cessionario_id: cessionarioFinal,
                endereco_instalacao: operacionais?.endereco_instalacao ?? null,
                // Manter status/coluna se existiam
                ...(operacionais?.status_id && { status_id: operacionais.status_id }),
                ...(operacionais?.quadro_atual && { quadro_atual: operacionais.quadro_atual }),
                ...(operacionais?.coluna_atual && { coluna_atual: operacionais.coluna_atual }),
                numero_pedido_operadora: operacionais?.numero_pedido_operadora ?? null,
                data_ativacao: operacionais?.data_ativacao ?? null,
                data_instalacao: (operacionais?.data_instalacao ?? null) as never,
              })
              .eq('id', item.id);

            if (!errUpdate) {
              operacionaisReaplicados++;
            } else {
              console.warn('⚠️ [REGENERAR] Falha ao reaplicar operacional no item:', item.id, errUpdate.message);
            }
          }
        }

        console.log('🔄 [REGENERAR] Operacionais reaplicados:', operacionaisReaplicados);

        const totalItens = novosItens?.length || 0;
        const falhasOperacionais = totalItens - operacionaisReaplicados;
        if (falhasOperacionais > 0) {
          toast.warning(`${falhasOperacionais} campo(s) operacional(is) não foram reaplicados`, {
            description: 'Alguns dados de operadora, DDD ou chip podem precisar ser preenchidos manualmente.',
          });
        }

        if (itensAgregadosRemover.length > 0) {
          const { error: erroLimpeza } = await supabase
            .from('itens_pedido')
            .delete()
            .in('id', itensAgregadosRemover.map((item) => item.id));
          if (erroLimpeza) {
            throw new Error(`Não foi possível substituir os itens retirados da proposta: ${erroLimpeza.message}`);
          }
        }

        // 10. Buscar status_lead_anterior e revisao_de_cancelado
        const { data: leadDataStatus } = await supabase
          .from('leads_carteira')
          .select('status_lead_anterior, revisao_de_cancelado')
          .eq('id', leadId)
          .single();

        const statusFinal = leadDataStatus?.status_lead_anterior || 'CONVERTIDO';
        const foiRevisaoCancelado = leadDataStatus?.revisao_de_cancelado === true;

        // 11. Atualizar snapshot no lead E limpar campos de revisão
        const novoSnapshot = {
          composicao: lead.proposta_composicao_cnpj,
          regenerado_em: new Date().toISOString(),
          regenerado_por: userId,
        };

        const { error: erroSnapshot } = await supabase
          .from('leads_carteira')
          .update({
            proposta_status: 'CONVERTIDA',
            proposta_fechada: true,
            proposta_snapshot: novoSnapshot,
            // LEI: recover NÃO troca status_lead (carteirizado/convertido não volta). Só limpa revisão.
            status_lead_anterior: null,
            revisao_pedido_id: null,
            revisao_iniciada_em: null,
            revisao_de_cancelado: false,
          })
          .eq('id', leadId);

        if (erroSnapshot) throw erroSnapshot;

        // 12. Invalidar queries (mesmos caches usados no fluxo de recuperação)
        // (Douglas 07/08, 2ª rodada) A tela do 360 continuava CANCELADO até o
        // F5. Duas causas somadas: (a) invalidar com [key, pedidoId] usa o id
        // da MÃE, mas item de pedido fechado migra pro FILHO — painel aberto
        // com outro id da família não casa a chave e não refaz; (b) o global é
        // refetchOnMount:false, então query inativa marcada stale não refazia
        // ao reabrir. Fix: invalidação AMPLA (sem id, pega mãe/filho) com
        // refetchType 'all', e um refetch AGUARDADO das ativas — o painel
        // visível vira RECUPERADO antes do toast, sem F5.
        queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo'], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: ['pedidos'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['cancelamentos-pedido'], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: ['pedido-detail'], refetchType: 'all' });
        await queryClient.refetchQueries({ queryKey: ['pedido-itens-completo'], type: 'active' });

        // Os itens que ficam continuam no cancelado com o próprio registro; o
        // motivo passa a ser o que o consultor escolheu (Douglas 23/10/2026).
        let ficaramCancelados = 0;
        if (isRevisaoCancelado && idsQueFicam.size > 0) {
          const rpc = supabase.rpc.bind(supabase) as unknown as (fn: string, args: Record<string, unknown>) =>
            PromiseLike<{ data: { itens?: number } | null; error: { message?: string } | null }>;
          const { data: mantidos, error: errMantidos } = await rpc('fn_manter_cancelado_na_recuperacao', {
            p_item_ids: [...idsQueFicam],
            p_motivo_id: opcoes?.motivoFicamId ?? null,
          });
          if (errMantidos) {
            toast.warning('O motivo dos itens que ficaram cancelados não foi gravado', {
              description: `${errMantidos.message ?? 'erro não informado'}. Eles continuam cancelados; o pedido foi recuperado.`,
              duration: 12000,
            });
          }
          ficaramCancelados = Number(mantidos?.itens ?? idsQueFicam.size);
        }

        const totalAplicados = itensParaRecuperar.length + novosCriados;
        toast.success(foiRevisaoCancelado ? 'Pedido recuperado com sucesso!' : 'Itens regenerados com sucesso!', {
          description: foiRevisaoCancelado
            ? `${totalAplicados} item(ns) — ${itensParaRecuperar.length} recuperado(s), ${novosCriados} novo(s) — movido(s) para PEDIDO RECUPERADO${ficaramCancelados > 0 ? ` · ${ficaramCancelados} ficou(aram) cancelado(s)` : ''}`
            : `${novosCriados} item(ns) criado(s), ${itensParaRecuperar.length} mantido(s), ${operacionaisReaplicados} com dados preservados`,
        });

        return {
          success: true,
          itensRegenerados: itensParaRecuperar.length + novosCriados,
          operacionaisPreservados: itensParaRecuperar.length,
        };

      } catch (converterError: any) {
        console.error('❌ [REGENERAR] Erro durante conversão:', converterError);
        throw converterError;
      }

    } catch (error: any) {
      console.error('❌ [REGENERAR] Erro fatal:', error);
      toast.error('Erro ao regenerar itens', {
        description: error.message,
      });
      return {
        success: false,
        itensRegenerados: 0,
        operacionaisPreservados: 0,
        error: error.message,
      };
    } finally {
      setRegenerando(false);
    }
  }, [converter, queryClient]);

  return { regenerar, regenerando };
}

/**
 * Função auxiliar para restaurar itens antigos em caso de erro
 * Remove qualquer item parcial e reinsere os originais que foram apagados.
 * 🔒 Itens protegidos (finalizados/instalados) NUNCA são tocados — nem no
 * delete de limpeza, nem no reinsert (eles nunca foram apagados).
 */
async function restaurarItensAntigos(
  pedidoId: string,
  itensApagados: any[] | null,
  protegidosIds?: Set<string>,
) {
  try {
    // Limpar qualquer item parcial — preservando os protegidos
    let deleteQuery = supabase
      .from('itens_pedido')
      .delete()
      .eq('pedido_id', pedidoId);
    if (protegidosIds && protegidosIds.size > 0) {
      deleteQuery = deleteQuery.not('id', 'in', `(${[...protegidosIds].join(',')})`);
    }
    await deleteQuery;

    // Restaurar itens originais que tinham sido apagados
    if (itensApagados && itensApagados.length > 0) {
      // Remover campos que podem causar conflito (id é gerado)
      const itensParaRestaurar = itensApagados.map(item => {
        const { id, created_at, updated_at, ...resto } = item;
        return resto;
      });

      await supabase
        .from('itens_pedido')
        .insert(itensParaRestaurar);

      console.log('🔙 [ROLLBACK] Itens restaurados com sucesso:', itensApagados.length);
      toast.info('Itens restaurados ao estado anterior');
    }
  } catch (rollbackError) {
    console.error('❌ [ROLLBACK] Falha ao restaurar:', rollbackError);
    toast.error('Erro crítico: não foi possível restaurar itens. Contate o suporte.');
  }
}
